import React, { createContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

// Set base URL for axios
const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create axios instance with base URL (memoized to prevent re-creation on every render)
  const api = useMemo(() => axios.create({
    baseURL: API_URL,
  }), []);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  };

  // Set auth header with token
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token, api]);

  // Load user data if token exists and is valid
  useEffect(() => {
    const loadUser = async () => {
      if (token && !isTokenExpired(token)) {
        try {
          const res = await api.get('/auth/me');
          setCurrentUser(res.data.data);
        } catch (err) {
          console.error('Failed to load user:', err);
          logout();
        }
      } else if (token) {
        // Token exists but is expired
        logout();
      }
      setLoading(false);
    };

    loadUser();
  }, [token, api]);

  // Login user
  const login = async (username, password) => {
    try {
      setError(null);
      const res = await api.post('/auth/login', { username, password });
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setCurrentUser(res.data.user);
      return true;
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Login failed. Please check your credentials.'
      );
      return false;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    delete api.defaults.headers.common['Authorization'];
    // Mark that we're logging out so initial loader doesn't show
    sessionStorage.setItem('logging_out', 'true');
    // Force a page reload to ensure clean state and redirect to login
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        error,
        login,
        logout,
        isAuthenticated: !!currentUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
